import React, { useEffect, useState } from "react";
import { View, TouchableOpacity } from "react-native";

import Text from "components/Text";
import Icon from "components/Icon";
import Input from "components/Input";
import Empty from "components/Empty";
import Error from "components/Error";
import Loading from "components/Loading";
import AnimatedInfinityList from "components/AnimatedInfinityList";

import { styles } from "./styles";
import { sizes, colors, strings } from "assets/constants";

import { useFlag } from "hooks";
import { liveSearchInput, mq } from "utils";

import { useCustomers } from "clients/apis";
import Separator from "components/Separator";
import ModalInfinityList from "components/ModalInfinityList";

let timer: ReturnType<typeof setTimeout>;

export default function CustomerPicker(props: PickerT) {
  const { value, onSelect } = props;
  const val = value as CustomerPickerItemT;

  const [name, setName] = useState("");
  const {
    refetch,
    isError,
    error: customerError,
    isRefetching,
    isLoading: customersLoading,
    data: customers,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCustomers({ name });

  const { flg: modal, on: show, off: hide } = useFlag();
  const { flg: typing, on: startTyping, off: stopTyping } = useFlag();

  const _onHide = () => {
    setName("");
    hide();
  };

  const [valueState, setValueState] = useState<
    CustomerPickerItemT | undefined
  >();

  useEffect(() => {
    setValueState(val);
  }, [val]);

  const _renderItem = (items: []) => {
    return items.map((customer: CustomerPickerItemT, key: number) => (
      <React.Fragment key={String(key)}>
        <TouchableOpacity
          style={styles.item}
          onPress={() => {
            onSelect && onSelect(customer);
            setName("");
            setValueState(customer);
            hide();
          }}
        >
          <Text>{customer.name}</Text>
        </TouchableOpacity>
        <Separator size={"80%"} />
      </React.Fragment>
    ));
  };

  const _renderList = () => {
    if (customersLoading || typing) {
      return <Loading style={{ marginTop: mq(20) }} />;
    }

    if (isError) {
      return (
        <Error error={customerError} reset={refetch} loading={isRefetching} />
      );
    }

    if (customers?.pages?.[0]?.data?.data?.length === 0) {
      return <Empty message={strings.no_customer} />;
    }

    return (
      <AnimatedInfinityList
        onRefresh={refetch}
        responseData={customers}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        renderItemList={_renderItem}
      />
    );
  };

  return (
    <>
      <TouchableOpacity
        onPress={show}
        activeOpacity={sizes.tapOpacity}
        style={{ ...styles.container }}
      >
        <View style={styles.iconContainer}>
          <Icon name="chevron-down" />
        </View>
        <View style={styles.textContainer}>
          <Text
            color={valueState ? colors.textColorDefault : colors.placeholder}
          >
            {valueState?.name || strings.pick_customer}
          </Text>
        </View>

        <View style={styles.iconContainer}>
          <Icon name="user" />
        </View>
      </TouchableOpacity>

      <ModalInfinityList close={_onHide} visible={modal}>
        <View style={styles.searchInputContainer}>
          <Input
            style={styles.searchInput}
            hideError
            icon={{ name: "search" }}
            placeholder={strings.search}
            onChangeText={(inputName) => {
              liveSearchInput(
                inputName,
                name,
                setName,
                timer,
                startTyping,
                stopTyping
              );
              refetch();
            }}
          />
        </View>
        {_renderList()}
      </ModalInfinityList>
    </>
  );
}
